// --------------------------------------------------------------
// Created On: 2026-11-02
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Modal from "src/components/Modal/Modal";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import Spinner from "src/components/Spinner/Spinner";
import Error from "src/components/Error/Error";
import useReduxSelector from "src/hooks/useReduxSelector";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import getCustomerServiceMessage from "src/utilities/getCustomerServiceMessage";
import PostDeviceClaimRequestBody from "src/types/api/PostDeviceClaimRequestBody";
import PostDeviceClaimResponseBody from "src/types/api/PostDeviceClaimResponseBody";
import { API } from "src/constants/miscellaneous";
import styles from "./ClaimDeviceModal.module.scss";

// Modal for claiming a device with its type, identifier, and claim code.
export default function ClaimDeviceModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [deviceIdentifierInput, setDeviceIdentifierInput] = useState<string>("");
  const [claimCodeInput, setClaimCodeInput] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Clears the form and closes the modal.
  function handleClose(): void {
    setDeviceIdentifierInput("");
    setClaimCodeInput("");
    setErrorMessage("");
    props.onClose();
  }

  // Claims the device and reports it to the parent if the claim was successful.
  async function handleClaim(): Promise<void> {
    const deviceIdentifier = deviceIdentifierInput.trim();
    const requestBody: PostDeviceClaimRequestBody = {
      deviceType: props.deviceType,
      deviceIdentifier: deviceIdentifier,
      claimCode: claimCodeInput.trim(),
    };

    setErrorMessage("");
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/device/claim`,
      "POST",
      requestBody
    )) as [Response, PostDeviceClaimResponseBody];

    if (response.ok && responseBody) {
      if (responseBody.deviceClaimed) {
        props.onDeviceClaimed(props.deviceType, deviceIdentifier);
        handleClose();
      } else if (responseBody.companyAlreadyClaimed) {
        setErrorMessage(
          `The device with identifier '${deviceIdentifier}' could not be claimed because it was already claimed by ` +
            "the current company."
        );
      } else {
        setErrorMessage(
          `The device with identifier '${deviceIdentifier}' and claim code '${requestBody.claimCode}' could not be ` +
            `claimed.\n${getCustomerServiceMessage()}`
        );
      }
    } else {
      setErrorMessage(await getApiError(response, "Unable to claim device."));
    }
    setLoading(false);
  }

  return (
    <div data-test="claim-device-modal">
      <Modal
        show={props.showModal}
        onHide={() => handleClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <Spinner loading={loading} />

        <ModalHeader>
          <h5>Claim Monitoring Device</h5>
        </ModalHeader>
        <ModalBody>
          <p>
            Claiming a {props.deviceTypeName} adds it to your company so it can be selected as a monitoring device. If
            yours is not showing up when trying to enter the identifier, enter its identifier and claim code below to
            claim it.
          </p>
          <div className="mb-3">
            <label htmlFor="claimDeviceIdentifierInput" className="form-label">
              Device Identifier
            </label>
            <input
              data-test="claim-device-identifier-input"
              type="text"
              className="form-control"
              id="claimDeviceIdentifierInput"
              autoComplete="off"
              disabled={loading}
              value={deviceIdentifierInput}
              onChange={(e) => setDeviceIdentifierInput(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="claimDeviceClaimCodeInput" className="form-label">
              Claim Code
            </label>
            <input
              data-test="claim-device-claim-code-input"
              type="text"
              className="form-control"
              id="claimDeviceClaimCodeInput"
              autoComplete="off"
              disabled={loading}
              value={claimCodeInput}
              onChange={(e) => setClaimCodeInput(e.target.value)}
            />
          </div>

          {errorMessage.length > 0 && <Error message={errorMessage} />}
        </ModalBody>
        <ModalFooter>
          <button
            data-test="claim-device-button"
            type="button"
            className="btn btn-primary"
            disabled={loading || deviceIdentifierInput.trim().length === 0 || claimCodeInput.trim().length === 0}
            onClick={() => handleClaim()}
          >
            Claim Device
          </button>
          <button
            data-test="cancel-claim-device-button"
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={() => handleClose()}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  deviceType: string;
  deviceTypeName: string;
  onDeviceClaimed: (deviceType: string, deviceIdentifier: string) => void;
  onClose: () => void;
}
